import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Switch,
  Button,
  Dialog,
  DialogContent,
  Typography,
  FormControl,
  TextField,
  Tooltip,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import WarnModal from "../../components/WarnModal";
import { generateResponsiveStyle } from "../../utils/ModalStyle";
import { Loader, showError, showToast } from "../../constants";
import {
  useLazyGetAllUsersQuery,
  useChangeUserStatusMutation,
  useLazyGetUserCsvQuery,
  useDeleteUserMutation,
  useUpdateUserMutation,
} from "../../services/users";
import { AllUser, CommonBody } from "../../types/General";
import Pagination from "../../components/Pagination";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { isNumber, isValidInput } from "../../utils/validations";
import useAuth from "../../hooks/useAuth";
import useTranslation from "../../hooks/Translation";

const ManageUsers = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [getAllUser] = useLazyGetAllUsersQuery();
  const [deleteUser] = useDeleteUserMutation();
  const [updateUser] = useChangeUserStatusMutation();
  const [userCsv] = useLazyGetUserCsvQuery();
  const [addCredit] = useUpdateUserMutation();
  const style = generateResponsiveStyle();
  const [open, setOpen] = useState(false);
  const [id, setId] = useState<string>("");
  const [creditModal, setCreditModal] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [allUsers, setAllUsers] = useState<AllUser[]>([]);
  const [credit, setCredit] = useState<string>("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openQueryModal, setOpenQueryModal] = React.useState(false);
  const [error, setError] = useState(false);
  const [normalCredit, setNormalCredit] = useState("");
  const [highlightedCredit, setHighlightedCredit] = useState("");
  const translate = useTranslation();
  const [hidePermission, setHidePermission] = useState<any>({
    isAdd: true,
    isDelete: true,
  });
  const [loading, setLoading] = useState(false);

  const getUserCsv = async () => {
    try {
      const response = await userCsv({
        type: "user",
        query: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        window?.open(response?.data);
      } else {
        console.log("error");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  const label = { inputProps: { "aria-label": "Switch demo" } };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleClose = () => {
    setOpenQueryModal(false);
  };

  let totalPages = Math.ceil(totalCount / 10);

  const getUsers = async () => {
    setLoading(true);
    try {
      const response = await getAllUser({
        query: debouncedSearchTerm.trim(),
        page: page,
        type: "user",
      }).unwrap();
      if (response?.statusCode === 200) {
        setLoading(false);
        setAllUsers(response?.data?.users || []);
        setTotalCount(response?.data?.totalCount);
      } else {
        setAllUsers([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteUser({ userId: id }).unwrap();
      if (response?.statusCode === 200) {
        showToast("User deleted successfully");
        getUsers();
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const updateUserStatus = async (id: string, status: boolean) => {
    const data = {
      isBlocked: status,
    };

    let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;

    const response = await updateUser({ encryptedBody, userId: id }).unwrap();
    if (response?.statusCode === 200) {
      const updatedData = allUsers.map((x) => {
        if (x._id === id) {
          return {
            ...x,
            isBlocked: status,
          };
        }
        return x;
      });
      setAllUsers(updatedData);
      showToast("Status updated successfully");
    } else {
      showError("Please try again");
    }
  };

  const handleClickOpen = (id: string) => {
    setOpenQueryModal(true);
    setId(id);
  };

  const handleAddCredit = async () => {
    const normalCreditValue = Number(normalCredit);
    const highlightedCreditValue = Number(highlightedCredit);

    if (normalCreditValue <= 0 && highlightedCreditValue <= 0) {
      showError("Please fill in at least one credit field");
      return;
    }

    const data = {
      normalCred: normalCredit || 0,
      highlightCred: highlightedCredit || 0,
    };
    console.log(data);

    try {
      let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;
      const response = await addCredit({ userId: id, encryptedBody }).unwrap();
      if (response?.statusCode === 200) {
        showToast("Credits added successfully");
        setOpenQueryModal(false);

        getUsers();
      }
    } catch (error: any) {
      if (error?.data?.message) {
        showError(error?.data?.message || "");
      }
    }
  };

  const checkPermission = () => {
    const permissions = userData?.permission;
    if (permissions?.length) {
      let idx = -1;
      idx = permissions?.findIndex(
        (ele: any) => ele?.name === "Manage Customers"
      );

      if (idx != -1) {
        let data = {
          isAdd: permissions[idx]?.isAdd,
          isDelete: permissions[idx]?.isDelete,
        };
        setHidePermission(data);
      }
    }
  };

  useEffect(() => {
    getUsers();
  }, [page, debouncedSearchTerm]);

  useEffect(() => {
    if (userData && userData?.permission?.length) {
      checkPermission();
    }
  }, [userData]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.user?.ManageUsers}</h1>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <Box className="cards_header_right">
              <Button className="btn btn_primary" onClick={() => getUserCsv()}>
                <FileDownloadIcon /> {translate?.user?.ExportCsv}
              </Button>
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Box className="heading"></Box>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{translate?.user?.SNo}</TableCell>

                  <TableCell>{translate?.user?.Name}</TableCell>
                  <TableCell>{translate?.user?.PhoneNo}</TableCell>
                  <TableCell>{translate?.user?.Email}</TableCell>
                  <TableCell>{translate?.user?.AccountStatus}</TableCell>
                  <TableCell>{translate?.user?.Credits}</TableCell>
                  <TableCell>{translate?.user?.Actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allUsers?.length > 0 ? (
                  allUsers?.map((row, i) => (
                    <TableRow key={row?._id}>
                      <TableCell align="center">
                        {(page - 1) * 10 + i + 1}
                      </TableCell>

                      <TableCell>{row?.fullName || "-"}</TableCell>

                      <TableCell>{`${row?.countryCode || ""} ${
                        row?.phone || "-"
                      }`}</TableCell>
                      <TableCell>{row?.email || "-"}</TableCell>

                      <TableCell>
                        <Switch
                          {...label}
                          size="small"
                          checked={!row?.isBlocked}
                          onChange={() =>
                            updateUserStatus(row?._id, !row?.isBlocked)
                          }
                        />
                      </TableCell>
                      <TableCell
                        className="addCredits"
                        onClick={() => handleClickOpen(row?._id)}
                      >
                       {translate?.user?.AddCredits}
                      </TableCell>
                      <TableCell>
                        <Box className="table_actions">
                          <Tooltip title="View" placement="top">
                            <IconButton
                              onClick={() =>
                                navigate(`/manageusers/userdetails/${row?._id}`)
                              }
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>

                          {hidePermission?.isAdd ? (
                            <Tooltip title="Edit" placement="top">
                              <IconButton
                                onClick={() =>
                                  navigate(`/manageusers/usersform/${row?._id}`)
                                }
                              >
                                <ModeEditIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {hidePermission?.isDelete ? (
                            <Tooltip title="Delete" placement="top">
                              <IconButton
                                onClick={() => {
                                  setOpen(true);
                                  setSelectedId(row?._id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No User Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {allUsers?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={allUsers}
            page={page}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        ) : (
          ""
        )}
      </div>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => handleDelete(selectedId)}
        name="User"
      />
      <Dialog
        open={openQueryModal}
        onClose={handleClose}
        className="revert_dialog"
      >
        <DialogContent>
          <Typography className="dialog_title">{translate?.user?.AddCredits}</Typography>

          <FormControl fullWidth>
            <Typography className="custom_label">{translate?.user?.NormalCredits}</Typography>
            <TextField
              hiddenLabel
              className="text_field"
              autoFocus
              name="normal"
              type="text"
              placeholder="Normal Credits"
              fullWidth
              onChange={(val) => {
                setNormalCredit(val.target.value);
              }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ paddingTop: 2 }}>
            <Typography className="custom_label">
               {translate?.user?.HighlightedCredits}
            </Typography>
            <TextField
              hiddenLabel
              className="text_field"
              autoFocus
              name="highlighted"
              type="text"
              placeholder="Highlighted Credits"
              fullWidth
              onChange={(val) => {
                setHighlightedCredit(val.target.value);
              }}
            />
          </FormControl>
          <Box className="form_btn">
            <Button className="btn" variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="btn btn_primary"
              type="submit"
              onClick={handleAddCredit}
            >
              Add
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </MainContainer>
  );
};

export default ManageUsers;
